import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  Space,
  Drawer,
  Form,
  Input,
  Select,
  Row,
  Col,
  Statistic,
  message,
  Tooltip,
  DatePicker,
  InputNumber,
} from "antd";
import { EyeOutlined } from "@ant-design/icons";
import EditIcon from "../../ui/EditIcon";
import { useNavigate } from "react-router-dom";
import useQuery from "../../../hooks/useQuery";
import {
  updateUrlSearchParams,
  parsePageParam,
  parseSearchParam,
} from "../../../helpers/urlFilters";
import {
  capitalizeFirstLetter,
  formatToINR,
  debounce,
} from "../../../helpers/utility";
import { getReceptionistOutPatientListApi } from "../../../services/receptionistop.services";
import { getCookie } from "../../../helpers/localStorage";
import { FacilityID } from "../../../constants/defaultKeys";
import axiosInstance from "../../../config/axios.config";
import OpMenu from "../../PatientsOP/OpMenu";
import inputStyles from "../../../styles/Custom/Input.module.css";
import dayjs from "dayjs";
import { syncBillsApi } from "../../../services/receptionist.services";
import { UiButton, UiTag, ActionIconButton, UiDataTable } from "../../ui";
import { formatPatientAge } from "../../../helpers/formatAge";
import useIsMobile from "../../../hooks/useIsMobile";
import { getTablePagination } from "../../../utils/tablePagination";
import useReceptionListTableScroll from "../../../hooks/useReceptionListTableScroll";
import "../receptionistMobileForms.css";
import styles from "./OutPatientTable.module.css";

export const OP_ADMISSION_PAGE_SIZE = 50;

const { Option } = Select;
const formatEmrName = (type) => {
  const map = {
    neuro: "Neuro EMR",
    ortho: "Ortho EMR",
    cardio: "Cardio EMR",
    sports: "Sports EMR",
    general: "General EMR",
    pediatric: "Pediatric EMR",
    cardiopulmonary: "Cardiopulmonary EMR",
  };
  return (
    map[type?.toLowerCase()] ||
    `${type?.charAt(0).toUpperCase() + type?.slice(1)} EMR`
  );
};

const patchUpdatePatient = (id, values, facilityId) => {
  const payload = {
    first_name: values.first_name || "",
    last_name: values.last_name || "",
    dob: values.patient_dob ? values.patient_dob.format("YYYY-MM-DD") : "",
    gender: values.gender,
    mobile: values.mobile || "",
    email: values.email || null,
    address: values.address || "",
    notes: values.notes || "",
    facility: facilityId,
  };
  return axiosInstance.patch(`accounts/update-patient/${id}/`, payload);
};

const OutPatientTable = ({ onCountChange }) => {
  const navigate = useNavigate();
  const params = useQuery();
  const page = parsePageParam(params);
  const searchQuery = parseSearchParam(params) || "";
  const isMobile = useIsMobile();
  const tableWrapRef = useRef(null);
  const tableScroll = useReceptionListTableScroll(tableWrapRef, isMobile);
  const [loading, setLoading] = useState(true);
  const [patientList, setPatientList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [form] = Form.useForm();
  const facilityId = Number(getCookie(FacilityID));
  function disabledDate(current) {
    return current && current > dayjs().endOf("day");
  }
  const fetchPatientList = () => {
    setLoading(true);
    const params = new URLSearchParams({
      facility: String(facilityId),
      page: String(page),
      limit: String(OP_ADMISSION_PAGE_SIZE),
    });
    if (searchQuery) {
      params.set("search", searchQuery);
      params.set("page", "1");
    }

    getReceptionistOutPatientListApi(params.toString())
      .then((res) => {
        const responseData = res?.data;
        const data = responseData?.data || [];
        const count = responseData?.total_count ?? data.length;
        setPatientList(data);
        setTotalCount(count);
        onCountChange?.(count);
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPatientList();
  }, [facilityId, searchQuery, page]);

  const handleEdit = (record) => {
    setEditRecord(record);
    const [firstName, ...lastNameParts] = (record.patient_name || "")
      .trim()
      .split(" ");
    const lastName = lastNameParts.join(" ");

    form.setFieldsValue({
      first_name: firstName,
      last_name: lastName,
      patient_dob: record.patient_dob ? dayjs(record.patient_dob) : null,
      gender: record.patient_gender,
      mobile: record.contact_no,
      email: record.patient_email || "",
      address: record.address || "",
      notes: record.notes || "",
      age: record.patient_age_by_dob ?? null, 
    });

    setIsDrawerOpen(true);
  };

  const handleView = (record) => {
    if (!record) return;
    const mappedPatient = {
      first_name: record.patient_name?.split(" ")[0] || "",
      last_name: record.patient_name?.split(" ").slice(1).join(" ") || "",
      age: record.patient_age,
      gender: record.patient_gender,
      op_id: record.op_id,
      patient_id: record.patient_id,
    };
    setSelectedPatient(mappedPatient);

    if (record.id) {
      navigate(
        `/receptionist/op/billing?admission=${record.id}&patient_id=${record.patient_id}`
      );
    }
  };

  const handleSave = () => {
    form
      .validateFields()
      .then((values) => {
        if (!editRecord) return;

        patchUpdatePatient(editRecord.patient_id, values, facilityId)
          .then(() => {
            message.success("Patient details updated successfully!");
            setIsDrawerOpen(false);
            fetchPatientList();
          })
          .catch((err) => {
            console.error(err);
            message.error("Failed to update patient details.");
          });
      })
      .catch((info) => console.log("Validation Failed:", info));
  };

  const onSearchChange = debounce((e) => {
    updateUrlSearchParams(navigate, {
      search: e.target.value || null,
      page: 1,
    });
  }, 300);

  const onSync = () => {
    syncBillsApi()
      .then((res) => {
        // message.success(res?.data?.message);
        fetchPatientList();
      })
      .catch((err) => message.error(err?.response?.data?.message));
  };

  const columns = [
    {
      title: "Consultation ID",
      dataIndex: "op_id",
      key: "op_id",
      width: 250,
    },
    {
      title: "Full Name",
      dataIndex: "patient_name",
      width: 300,
      sorter: (a, b) => a?.patient_name?.localeCompare(b?.patient_name),
      render: (_, record) => (
        <Row>
          <Col span={10}>{capitalizeFirstLetter(record?.patient_name)}</Col>

          <Col span={7}>
            <UiTag tone="neutral" size="sm">
              {formatPatientAge(record?.patient_age_by_dob, record?.patient_gender)}
            </UiTag>
          </Col>
        </Row>
      ),
    },
    {
      title: "Service Type",
      dataIndex: "emr_service_type",
      key: "emr_service_type",
      width: 250,
      render: (value) =>
        value ? (
          <UiTag tone="success" size="sm" style={{ textTransform: "capitalize" }}>
            {formatEmrName(value)}
          </UiTag>
        ) : (
          "-"
        ),
    },

    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      render: (amount) =>
        amount < 0 ? (
          <Statistic
            value={Math.abs(amount)}
            valueStyle={{ color: "#40C164", fontSize: "16px" }}
            prefix="+"
          />
        ) : amount > 0 ? (
          <Statistic
            value={formatToINR(amount)}
            valueStyle={{ color: "#FD6050", fontSize: "16px" }}
            prefix="-"
          />
        ) : (
          <Statistic
            value={formatToINR(amount)}
            valueStyle={{ color: "#40C164", fontSize: "16px" }}
          />
        ),
    },

    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <UiButton variant="ghost" size="md" onClick={() => handleView(record)}>
            View
          </UiButton>
          <ActionIconButton aria-label="Edit patient" onClick={() => handleEdit(record)}>
            <EditIcon />
          </ActionIconButton>
          <Tooltip
            title={
              <>
                <div>Consultant: {record?.doctor_name || "N/A"}</div>
                <div>
                  Service:{" "}
                  {record?.emr_service_type
                    ? `${capitalizeFirstLetter(record.emr_service_type)} EMR`
                    : "N/A"}
                </div>
              </>
            }
          >
            <EyeOutlined />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      {selectedPatient && (
        <OpMenu patientData={selectedPatient} navigate={navigate} />
      )}
      {/* <Row style={{ marginBottom: 16 }} gutter={16} align="middle">
        <Col span={8} style={{ display: "flex", alignItems: "center" }}>
          <Input.Search
            style={{ width: "93%", marginBottom: "10px" }}
            placeholder="Search for patient"
            allowClear
            size="large"
            onChange={onSearchChange}
          />
        </Col>
        <Col>
          <span style={{ fontWeight: 500, marginLeft: 0 }}>
            Total Records: {patientList.length}
          </span>
        </Col>
      </Row> */}
      <UiDataTable
        showHeader={false}
        shellClassName="reception-patient-list-shell"
        onSearch={onSearchChange}
        searchValue={searchQuery}
        searchPlaceholder="Search for patient"
        toolbarTrailing={
          <UiButton
            onClick={onSync}
            variant="ghost"
            size="md"
            className={styles.outpatientSyncBtn}
          >
            Sync Bills
          </UiButton>
        }
      >
        <div ref={tableWrapRef} className="reception-patient-list-table">
          <Table
            className="no-vertical-borders"
            rowKey={(record) => record.id}
            dataSource={patientList}
            columns={columns}
            loading={loading}
            pagination={getTablePagination({
              current: page,
              pageSize: OP_ADMISSION_PAGE_SIZE,
              total: totalCount,
              onChange: (p) => updateUrlSearchParams(navigate, { page: p }),
            })}
            scroll={tableScroll}
          />
        </div>
      </UiDataTable>

      <Drawer
        title="Edit Patient"
        placement="right"
        width={isMobile ? "100%" : 600}
        rootClassName={isMobile ? "reception-drawer-fullscreen" : undefined}
        onClose={() => {
          setIsDrawerOpen(false);
          setEditRecord(null);
        }}
        open={isDrawerOpen}
        footer={
          <div
            className={isMobile ? "reception-drawer-footer-row" : undefined}
            style={!isMobile ? { textAlign: "right" } : undefined}
          >
            <UiButton
              variant="secondary"
              size="md"
              style={!isMobile ? { marginRight: 8 } : undefined}
              onClick={() => setIsDrawerOpen(false)}
            >
              Cancel
            </UiButton>
            <UiButton variant="primary" size="md" onClick={handleSave}>
              Save and Proceed
            </UiButton>
          </div>
        }
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} span={12}>
              <Form.Item
                label="First Name"
                name="first_name"
                rules={[{ required: true, message: "Please enter first name" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} span={12}>
              <Form.Item
                label="Last Name"
                name="last_name"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} span={12}>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Date Of Birth"
                    name="patient_dob"
                    rules={[{ required: true, message: "Enter DOB or Age" }]}
                  >
                    <DatePicker
                      disabledDate={disabledDate}
                      className={inputStyles.mdDateInput}
                      style={{ width: "100%" }}
                      placeholder="Select or auto-calculated"
                      onChange={(date) => {
                        if (date) {
                          const today = dayjs();
                          const age = today.diff(date, "year");
                          form.setFieldValue("age_by_dob", age);
                        } else {
                          form.setFieldValue("age_by_dob", null);
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Age" name="age">
                    <InputNumber
                      min={0}
                      max={120}
                      precision={0}
                      step={1}
                      style={{ width: "100%" }}
                      placeholder="Enter Age"
                      onChange={(age) => {
                        if (age !== null && age !== undefined) {
                          const year = dayjs().year() - age;
                          const dob = dayjs(`${year}-01-01`);
                          form.setFieldValue("patient_dob", dob);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (["-", "+", "e", ".", ","].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
            <Col xs={24} span={12}>
              <Form.Item
                label="Gender"
                name="gender"
                rules={[{ required: true, message: "Please select gender" }]}
              >
                <Select>
                  <Option value="Male">Male</Option>
                  <Option value="Female">Female</Option>
                  <Option value="Other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} span={12}>
              <Form.Item
                label="Mobile"
                name="mobile"
                rules={[
                  { required: true, message: "Please enter mobile number" },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} span={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { type: "email", message: "Please enter a valid email" },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>
    </>
  );
};

export default OutPatientTable;
